/**
 * Design Tokens - Nice Ice Design System
 * 
 * This file centralizes all design tokens for consistent UI/UX across the application.
 * Use these tokens instead of direct values in components.
 */

// Color System
export const colors = {
  // Primary brand colors
  primary: {
    50: 'bg-teal-50 text-teal-900',
    100: 'bg-teal-100 text-teal-900',
    200: 'bg-teal-200 text-teal-900',
    300: 'bg-teal-300 text-teal-900',
    400: 'bg-teal-400 text-teal-900',
    500: 'bg-teal-500 text-white',
    600: 'bg-teal-600 text-white',
    700: 'bg-teal-700 text-white', // Brand primary
    800: 'bg-teal-800 text-white',
    900: 'bg-teal-900 text-white',
  },
  
  // Secondary accent colors
  secondary: {
    50: 'bg-blue-50 text-blue-900',
    100: 'bg-blue-100 text-blue-900',
    200: 'bg-blue-200 text-blue-900',
    300: 'bg-blue-300 text-blue-900',
    400: 'bg-blue-400 text-blue-900',
    500: 'bg-blue-500 text-white',
    600: 'bg-blue-600 text-white', // Brand secondary
    700: 'bg-blue-700 text-white',
    800: 'bg-blue-800 text-white',
    900: 'bg-blue-900 text-white',
  },
  
  // Neutral colors
  neutral: {
    50: 'bg-gray-50 text-gray-900',
    100: 'bg-gray-100 text-gray-900',
    200: 'bg-gray-200 text-gray-900',
    300: 'bg-gray-300 text-gray-900',
    400: 'bg-gray-400 text-gray-900',
    500: 'bg-gray-500 text-white',
    600: 'bg-gray-600 text-white',
    700: 'bg-gray-700 text-white',
    800: 'bg-gray-800 text-white',
    900: 'bg-gray-900 text-white',
  },
  
  // Text colors
  text: {
    primary: 'text-gray-900',
    secondary: 'text-gray-600',
    tertiary: 'text-gray-400',
    inverse: 'text-white',
    accent: 'text-teal-700',
    link: 'text-blue-600',
  },
  
  // Background colors
  background: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    tertiary: 'bg-gray-100',
    accent: 'bg-teal-50',
    accentSecondary: 'bg-blue-50',
  },
  
  // Border colors
  border: {
    light: 'border-gray-200',
    medium: 'border-gray-300',
    dark: 'border-gray-400',
    accent: 'border-teal-200',
    accentSecondary: 'border-blue-200',
  },
};

// Border Radius System
export const radius = {
  none: '',
  sm: 'rounded',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  xxl: 'rounded-2xl',
  full: 'rounded-full',
};

// Shadow System
export const shadows = {
  none: '',
  sm: 'shadow-sm',
  md: 'shadow',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  inner: 'shadow-inner',
};

// Typography System
export const typography = {
  // Font sizes with responsive variants
  // Each size includes desktop, tablet, and mobile sizes
  fontSize: {
    // Page titles (h1)
    h1: {
      desktop: 'text-5xl lg:text-6xl',
      tablet: 'text-4xl sm:text-5xl',
      mobile: 'text-3xl',
    },
    // Section titles (h2)
    h2: {
      desktop: 'text-4xl',
      tablet: 'text-3xl',
      mobile: 'text-2xl',
    },
    // Subsection titles (h3)
    h3: {
      desktop: 'text-2xl',
      tablet: 'text-xl',
      mobile: 'text-lg',
    },
    // Card titles (h4)
    h4: {
      desktop: 'text-xl',
      tablet: 'text-lg',
      mobile: 'text-base',
    },
    // Body text
    body: {
      large: 'text-lg',
      base: 'text-base',
      small: 'text-sm',
      xs: 'text-xs',
    },
  },
  
  // Font weights
  fontWeight: {
    light: 'font-light',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    extrabold: 'font-extrabold',
  },
  
  // Line heights
  lineHeight: {
    none: 'leading-none',
    tight: 'leading-tight',
    snug: 'leading-snug',
    normal: 'leading-normal',
    relaxed: 'leading-relaxed',
    loose: 'leading-loose',
  },
  
  // Letter spacing
  letterSpacing: {
    tighter: 'tracking-tighter',
    tight: 'tracking-tight',
    normal: 'tracking-normal',
    wide: 'tracking-wide',
    wider: 'tracking-wider',
    widest: 'tracking-widest',
  },
  
  // Pre-defined text styles for common elements
  textStyles: {
    // Page title (h1)
    pageTitle: 'text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight',
    // Section title (h2)
    sectionTitle: 'text-3xl md:text-4xl font-bold text-gray-900',
    // Subsection title (h3)
    subsectionTitle: 'text-2xl font-bold text-gray-900',
    // Card title (h4)
    cardTitle: 'text-xl font-bold text-gray-900',
    // Body text
    bodyLarge: 'text-lg text-gray-600 leading-relaxed',
    bodyBase: 'text-base text-gray-600',
    bodySmall: 'text-sm text-gray-600',
    // Special text
    accent: 'text-teal-700 font-medium',
    caption: 'text-sm text-gray-500',
    badge: 'text-xs font-semibold',
  },
};

// Spacing System
export const spacing = {
  // Using Tailwind's default spacing scale
  // This is just for reference
};

// Component-specific tokens
export const components = {
  // Button variants
  button: {
    primary: `bg-teal-700 hover:bg-teal-600 text-white ${radius.lg} ${shadows.md}`,
    secondary: `bg-blue-600 hover:bg-blue-500 text-white ${radius.lg} ${shadows.md}`,
    outline: `bg-transparent border border-teal-700 text-teal-700 hover:bg-teal-50 ${radius.lg}`,
    ghost: `bg-transparent text-teal-700 hover:bg-teal-50 ${radius.lg}`,
  },
  
  // Card variants
  card: {
    primary: `${radius.xl} ${shadows.lg} bg-white`,
    secondary: `${radius.xl} ${shadows.md} bg-gray-50`,
    accent: `${radius.xl} ${shadows.lg} bg-teal-50`,
  },
  
  // Focus styles
  focus: {
    primary: 'focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2',
    secondary: 'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
  },
  
  // Hover animations
  hover: {
    lift: 'hover:-translate-y-1 transition-transform duration-200',
    scale: 'hover:scale-105 transition-transform duration-200',
    shadow: 'hover:shadow-lg transition-shadow duration-200',
    combined: 'hover:-translate-y-1 hover:shadow-lg transition-all duration-200',
  },
};

// Create a named object for export
const designTokens = {
  colors,
  radius,
  shadows,
  typography,
  spacing,
  components,
};

export default designTokens; 